import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconPhoneOutgoing,
  IconPhoneCheck,
  IconTransfer,
  IconMessage,
  IconScissors,
  IconAlertTriangle,
  IconPhoneOff,
  IconPhoneX,
} from '@tabler/icons-react';
import { logWarning } from '@lib/logger/logWarning';
import { dispositionBadgeStyle } from '../constants/dispositionColors';
import type { ContactTimeline, CallPathEvent } from '../types/dashboard.types';

interface CallPathProps {
  timeline: ContactTimeline | null;
  isLoading: boolean;
  isError: boolean;
  /** The action the table's Action column reports, highlighted in the path. */
  shownAction?: string;
}

interface StepStyle {
  icon: React.ReactNode;
  /** Background, icon colour and border for the step's circle. */
  ring: string;
}

/**
 * Icon and colour per action, so the shape of a call reads before the text.
 *
 * `transferred` and `voicemail` get the strong colours because they are what
 * decide the Action column; `called` and `call_ended` are bookkeeping.
 */
function stepStyle (action: string): StepStyle {
  switch (action) {
  case 'called':
    return {
      icon: <IconPhoneOutgoing size={18} />,
      ring: 'bg-muted text-muted-foreground border-border',
    };
  case 'answered':
    return {
      icon: <IconPhoneCheck size={18} />,
      ring: 'bg-green-500/10 text-green-500 border-green-500/40',
    };
  case 'transferred':
    return {
      icon: <IconTransfer size={18} />,
      ring: 'bg-blue-500/10 text-blue-500 border-blue-500/60',
    };
  case 'voicemail':
    return {
      icon: <IconMessage size={18} />,
      ring: 'bg-orange-500/10 text-orange-500 border-orange-500/40',
    };
  case 'cut_off':
    return {
      icon: <IconScissors size={18} />,
      ring: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/40',
    };
  case 'transfer_error':
    return {
      icon: <IconAlertTriangle size={18} />,
      ring: 'bg-red-500/10 text-red-500 border-red-500/40',
    };
  case 'not_answered':
    return {
      icon: <IconPhoneX size={18} />,
      ring: 'bg-red-500/10 text-red-500 border-red-500/40',
    };
  default:
    return {
      icon: <IconPhoneOff size={18} />,
      ring: 'bg-muted text-muted-foreground border-border',
    };
  }
}

/** mm:ss from the first event of the path. */
function stamp (seconds: number): string {
  const whole = Math.floor(seconds);
  const mins = Math.floor(whole / 60);
  const secs = whole % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

/**
 * Actions a repeat of which means two CALLS were merged, not one event written
 * twice — a call has exactly one `called`, so seeing two means the split by
 * callLog boundaries could not separate them (73 of 117 multi-call contacts
 * have fewer `called` metrics than callLogs). Collapsing these would hide a
 * real call, along with everything that happened on it.
 *
 * Everything else collapses. A call cannot leave two voicemails or be cut off
 * twice, so a repeat there is the write-path defect: the backend decides
 * whether to record a metric by reading the callLog's previous status and
 * comparing, which is not atomic, so concurrent requests all insert.
 *
 * Measured within a single call: `voicemail` and `cut_off` repeats have a
 * median gap of 0.4s and 0.5s, while `called` and `answered` repeats have
 * medians of 119s and 25s. An elapsed-time threshold cannot separate them —
 * `voicemail` reaches 62.9s and `answered` starts at 2.2s — but the action
 * itself does, cleanly.
 */
const NEVER_COLLAPSED = new Set(['called', 'answered', 'transferred']);

interface PathStep {
  /** The first event of the run; supplies the label and the timestamp. */
  event: CallPathEvent;
  /** The last of the run, so the gap to the NEXT step spans the whole burst. */
  last: CallPathEvent;
  count: number;
}

/**
 * Collapses a burst of identical actions into one step.
 *
 * The collapse is SILENT on screen. Customers must not be shown a defect in
 * how we record events, so the count never renders — but it is logged, so the
 * bug stays measurable for engineering instead of disappearing entirely.
 *
 * Generic by default: today the duplicates are `cut_off` and `voicemail`, and
 * any other action that starts duplicating collapses the same way with no code
 * change — except for the few in NEVER_COLLAPSED, where a repeat means
 * something else entirely.
 */
function collapseRepeats (events: CallPathEvent[]): PathStep[] {
  const steps: PathStep[] = [];

  events.forEach((event) => {
    const current = steps[steps.length - 1];
    const isDuplicate =
      current !== undefined &&
      current.last.action === event.action &&
      !NEVER_COLLAPSED.has(event.action);

    if (isDuplicate) {
      current.count += 1;
      current.last = event;
      return;
    }

    steps.push({ event, last: event, count: 1 });
  });

  return steps;
}

function CallPathSkeleton (): React.JSX.Element {
  return (
    <div className="flex items-start justify-center gap-4 py-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col items-center gap-2">
          <div className="h-11 w-11 rounded-full bg-muted animate-pulse" />
          <div className="h-3 w-16 rounded bg-muted animate-pulse" />
        </div>
      ))}
    </div>
  );
}

export default function CallPath ({
  timeline,
  isLoading,
  isError,
  shownAction,
}: CallPathProps): React.JSX.Element {
  const { t } = useTranslation();

  const steps = collapseRepeats(timeline?.events ?? []);
  // A stable primitive: `steps` is a fresh array every render, so depending on
  // it directly would re-fire the log on each one.
  const bursts = steps
    .filter((step) => step.count > 1)
    .map((step) => `${step.event.action}x${step.count}`)
    .join(', ');

  useEffect(() => {
    if (bursts === '') {
      return;
    }

    // showToast=false: engineering sees this in BetterStack, the customer sees
    // nothing. These bursts are a write-path defect — the backend decides
    // whether to record a metric by reading the callLog's previous status and
    // comparing, which is not atomic, so concurrent requests all insert.
    void logWarning(
      `Duplicate call path events collapsed: ${bursts}`,
      'Dashboard/CallPath',
      { bursts },
      false,
    );
  }, [bursts]);

  if (isLoading) {
    return <CallPathSkeleton />;
  }

  if (isError) {
    return (
      <p className="text-xs text-muted-foreground py-2">
        {t('dashboard.callPath.error', { defaultValue: 'Could not load the call path.' })}
      </p>
    );
  }

  if (timeline === null || timeline.events.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-2">
        {t('dashboard.callPath.empty', { defaultValue: 'No events recorded for this call.' })}
      </p>
    );
  }

  const { events } = timeline;
  // Only the closing event carries a real disposition; the rest are
  // `default_action` placeholders and would be noise on every step.
  const finalDisposition = events[events.length - 1].disposition;
  const hasDisposition = finalDisposition !== null && finalDisposition !== 'default_action';

  /**
   * The gap INTO `call_ended` is not call time — that event is written when the
   * SDR classifies, not when the line drops. Measured across the database:
   * `called → answered` averages 12s (max 57s, real ring time), while
   * `transferred → call_ended` averages 63s and peaks at 6,569s. Labelling it
   * as elapsed call time would be a lie, so it gets its own wording.
   */
  const isClassificationGap = (next: CallPathEvent): boolean => next.action === 'call_ended';

  return (
    <div className="py-2">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
          {t('dashboard.callPath.title', { defaultValue: 'Call path' })}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {t('dashboard.callPath.steps', { defaultValue: '{{count}} steps', count: steps.length })}
        </span>
        <span
          className="text-[11px] text-muted-foreground ml-auto"
          title={t('dashboard.callPath.spanHelp', {
            defaultValue:
              'Time between the first and last recorded event. Not the length of the call — the closing event is written when the SDR classifies it.',
          })}
        >
          {t('dashboard.callPath.span', {
            defaultValue: 'first to last event: {{seconds}}s',
            seconds: timeline.durationSeconds,
          })}
        </span>
      </div>

      {/* Horizontal rail. Scrolls rather than wrapping: 94% of paths are 2-4
          steps, but the tail runs to 200 and a wrapped rail loses its shape.
          The inner `w-max mx-auto` centres short paths without clipping long
          ones — `justify-center` on the scroller would make the first steps
          unreachable once the rail overflows. */}
      <div className="overflow-x-auto pb-1">
        <div className="flex items-start w-max mx-auto">
          {steps.map((step, index) => {
            const { event } = step;
            const style = stepStyle(event.action);
            const next = steps[index + 1];
            const isShown = shownAction !== undefined && event.action === shownAction;
            // The gap runs from the END of this burst to the start of the next,
            // so collapsing never invents time that did not pass.
            const gap =
              next === undefined
                ? 0
                : Number((next.event.offsetSeconds - step.last.offsetSeconds).toFixed(1));

            return (
              <React.Fragment key={`${event.action}-${event.at}-${index}`}>
                <div className="flex flex-col items-center shrink-0 w-24 text-center">
                  <div
                    className={`h-11 w-11 rounded-full border-2 flex items-center justify-center ${style.ring}`}
                  >
                    {style.icon}
                  </div>
                  <span className="text-xs font-medium text-foreground mt-2 capitalize">
                    {t(`dashboard.actions.${event.action}`, event.action)}
                  </span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {stamp(event.offsetSeconds)}
                  </span>
                  {isShown && (
                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded mt-1 bg-blue-500/15 text-blue-500">
                      {t('dashboard.callPath.shown', { defaultValue: 'SHOWN' })}
                    </span>
                  )}
                </div>

                {next !== undefined && (
                  <div className="flex flex-col items-center shrink-0 w-16 pt-5">
                    <div className="w-full h-0.5 bg-border" />
                    <span className="text-[10px] text-muted-foreground tabular-nums mt-1.5 text-center leading-tight">
                      {isClassificationGap(next.event)
                        ? t('dashboard.callPath.classifiedAfter', {
                          defaultValue: 'classified {{seconds}}s later',
                          seconds: gap,
                        })
                        : `+${gap}s`}
                    </span>
                  </div>
                )}
              </React.Fragment>
            );
          })}

          {hasDisposition && (
            <div className="flex flex-col items-center shrink-0 pl-3 pt-3">
              {/* The disposition's own colour, the same one the calls table and
                  the overview chart use. It was purple for every outcome, so
                  the step that ends the path said nothing about how it ended —
                  and contradicted the badge on the row right above it. */}
              <span
                className="text-[11px] font-medium px-2 py-1 rounded border"
                style={dispositionBadgeStyle(finalDisposition)}
              >
                {t(`dashboard.dispositions.${finalDisposition}`, finalDisposition ?? '')}
              </span>
            </div>
          )}
        </div>
      </div>

      {timeline.truncated && (
        <p className="text-[10px] text-muted-foreground mt-3 text-center">
          {t('dashboard.callPath.truncated', {
            defaultValue: 'Only the first {{count}} events are shown — this call recorded more.',
            count: events.length,
          })}
        </p>
      )}

      {/*
        The path is scoped to THIS row's call, but the reader still needs to know
        the contact was called more than once — otherwise a path that starts at
        `called` with no outcome looks like data is missing rather than like the
        first of two attempts.
      */}
      {timeline.dialCount > 1 && (
        <p className="text-[10px] text-muted-foreground mt-2 text-center">
          {timeline.dialIndex !== null
            ? t('dashboard.callPath.dialPosition', {
              defaultValue: 'Call {{index}} of {{count}} to this contact in this session.',
              index: timeline.dialIndex,
              count: timeline.dialCount,
            })
            : t('dashboard.callPath.multipleDials', {
              defaultValue:
                'This contact was dialled {{count}} times in this session. The events of all those calls are shown together.',
              count: timeline.dialCount,
            })}
        </p>
      )}
    </div>
  );
}
